import React from 'react';
import { useDesign } from '../../designs';

interface TimeSpinnerProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
}

// Convert 24h "HH:MM" to 12h "h:MM AM/PM"
export function formatTime12h(time24: string): string {
  if (!time24) return '';
  const [hStr, mStr] = time24.split(':');
  let h = parseInt(hStr, 10);
  const m = mStr || '00';
  const ampm = h >= 12 ? 'PM' : 'AM';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${m} ${ampm}`;
}

// Parse "HH:MM" 24h → { hour12, minute, ampm }
function parse24(time24: string): { h12: number | null; m: number; ampm: 'AM' | 'PM' } {
  if (!time24) return { h12: null, m: 0, ampm: 'AM' };
  const [hStr, mStr] = time24.split(':');
  let h = parseInt(hStr, 10);
  const m = parseInt(mStr || '0', 10);
  const ampm: 'AM' | 'PM' = h >= 12 ? 'PM' : 'AM';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return { h12: h, m, ampm };
}

// Combine 12h parts → "HH:MM" 24h
function combine(h12: number, m: number, ampm: 'AM' | 'PM'): string {
  let h24 = h12 % 12;
  if (ampm === 'PM') h24 += 12;
  return `${h24.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
}

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5); // 0, 5, 10 ... 55

export default function TimeSpinner({ label, value, onChange }: TimeSpinnerProps) {
  const design = useDesign();
  const d = design.tokens;
  const fs = design.typography.fontSize;

  const parsed = parse24(value);
  const hour = parsed.h12;
  const minute = parsed.m;
  const ampm = parsed.ampm;

  const update = (next: { h12?: number | null; m?: number; ampm?: 'AM' | 'PM' }) => {
    const nextH = next.h12 !== undefined ? next.h12 : hour;
    const nextM = next.m !== undefined ? next.m : minute;
    const nextAP = next.ampm !== undefined ? next.ampm : ampm;
    if (nextH == null) {
      onChange('');
      return;
    }
    onChange(combine(nextH, nextM, nextAP));
  };

  const baseSelect: React.CSSProperties = {
    padding: '10px 6px',
    fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyInput, fontWeight: 400,
    color: hour != null ? d.textPrimary : d.textSecondary,
    backgroundColor: d.bgSurface,
    border: '1px solid rgba(200,146,58,0.10)',
    borderRadius: '2px', outline: 'none', boxSizing: 'border-box',
    textAlign: 'center', minWidth: 0,
  };

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <label style={{
        display: 'block', fontFamily: "'Raleway', sans-serif",
        fontSize: fs.sectionHeader, fontWeight: 700, letterSpacing: '1.5px',
        textTransform: 'uppercase', color: d.textSecondary, marginBottom: '6px',
      }}>{label}</label>
      <div style={{ display: 'flex', gap: '4px' }}>
        <select
          value={hour ?? ''}
          onChange={(e) => {
            const v = e.target.value;
            update({ h12: v === '' ? null : parseInt(v, 10) });
          }}
          style={{ ...baseSelect, flex: 1 }}
        >
          <option value="">—</option>
          {HOURS.map((h) => <option key={h} value={h}>{h}</option>)}
        </select>
        <select
          value={minute}
          onChange={(e) => update({ m: parseInt(e.target.value, 10) })}
          disabled={hour == null}
          style={{ ...baseSelect, flex: 1, opacity: hour == null ? 0.5 : 1 }}
        >
          {MINUTES.map((m) => (
            <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>
          ))}
        </select>
        <select
          value={ampm}
          onChange={(e) => update({ ampm: e.target.value as 'AM' | 'PM' })}
          disabled={hour == null}
          style={{ ...baseSelect, flex: 1, opacity: hour == null ? 0.5 : 1 }}
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
    </div>
  );
}
